import { memo, forwardRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { designTokens } from '@design-system';

import {
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  alpha,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { activeItem } from '@/store/reducers/menu';
import IconRetrieve from '../../IconRetrieve';
import useInboxCount from '@/views/approvals/Inbox/useInboxCount';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import { useMenuFavourites } from './favourites';

const INBOX = '/approvals/inbox';

/** the number of approvals waiting, beside My Approvals */
/** star beside a page: adds it to / removes it from Favourites at the top of the menu */
const FavStar = ({ id, title }: { id: string; title: string }) => {
  const { ids, toggle, full, max } = useMenuFavourites();
  const on = ids.includes(id);
  const label = on ? `Remove ${title} from favourites` : full ? `Favourites are full (${max}); remove one first` : `Add ${title} to favourites`;
  return (
    <Box
      component="span"
      role="button"
      aria-label={label}
      aria-pressed={on}
      title={label}
      className={on ? 'fav-star fav-on' : 'fav-star'}
      onClick={(e: React.MouseEvent) => {
        // the row is a link: the star must not open the page
        e.preventDefault();
        e.stopPropagation();
        if (on || !full) toggle(id);
      }}
      sx={{
        ml: 0.5,
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '6px',
        p: '2px',
        cursor: 'pointer',
        color: on ? '#fbbf24' : designTokens.colors.neutral[500],
        '&:hover': { color: '#fbbf24', backgroundColor: alpha('#fbbf24', 0.12) },
      }}
    >
      {on ? <StarRoundedIcon sx={{ fontSize: 17 }} /> : <StarBorderRoundedIcon sx={{ fontSize: 17 }} />}
    </Box>
  );
};

const InboxBadge = () => {
  const { data } = useInboxCount();
  if (!data) return null;
  return (
    <Box
      component="span"
      aria-label={`${data} approvals waiting`}
      sx={{ ml: 1, minWidth: 20, height: 20, px: 0.75, borderRadius: '10px', bgcolor: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontVariantNumeric: 'tabular-nums' }}
    >
      {data > 999 ? '999+' : data}
    </Box>
  );
};

interface NavItemProps {
  item: {
    id: string;
    url: string;
    target?: string;
    external?: boolean;
    disabled?: boolean;
    icon?: string;
    title: string;
    chip?: {
      color: 'primary' | 'secondary';
      variant: 'filled' | 'outlined';
      size: 'small' | 'medium';
      label: string;
      avatar?: React.ReactNode;
    };
  };
  level: number;
  activeItemId: string;
  onToggle: (id: string, items: any) => void;
}

const NavItem = memo<NavItemProps>(function NavItem({
  item,
  level,
  activeItemId,
  onToggle,
}) {
  const dispatch = useAppDispatch();

  const { drawerOpen, openItem } = useAppSelector((state) => state.menu);
  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps: any = {
    component: forwardRef<HTMLAnchorElement>((props, ref) => (
      <Link ref={ref} {...props} to={item.url} target={itemTarget} />
    )),
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = useCallback(
    (id: any) => {
      dispatch(activeItem({ openItem: [id] }));
    },
    [dispatch]
  );

  const isSelected = activeItemId
    ? activeItemId === item.id
    : openItem.findIndex((id) => id === item.id) > -1;

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      onClick={() => {
        itemHandler(item.id);
        onToggle(item.id, item);
      }}
      // keyboard navigation of the menu (Navigation/index.tsx)
      data-nav-id={item.id}
      data-nav-kind="item"
      data-nav-level={level}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level * 10 + 10}px` : 1.25,
        pr: 1.5,
        py: level === 1 ? 1.15 : 0.9,
        minHeight: level === 1 ? 46 : 40,
        borderRadius: '9px',
        mx: 0.75,
        mb: 0.35,
        transition: `all ${designTokens.transitions.duration.normal} ${designTokens.transitions.timing.easeInOut}`,
        backgroundColor: isSelected
          ? alpha(designTokens.colors.primary[500], 0.15)
          : 'transparent',
        border: isSelected
          ? `1px solid ${alpha(designTokens.colors.primary[500], 0.35)}`
          : '1px solid transparent',
        boxShadow: isSelected
          ? `inset 3px 0 0 ${designTokens.colors.primary[500]}`
          : 'inset 3px 0 0 transparent',
        '&:hover': {
          backgroundColor: alpha(designTokens.colors.primary[500], 0.1),
          borderColor: alpha(designTokens.colors.primary[500], 0.2),
        },
        // favourite star: shown on hover / keyboard focus, always when the page is a favourite
        '& .fav-star:not(.fav-on)': { opacity: 0 },
        '&:hover .fav-star, &:focus-visible .fav-star, &.Mui-focusVisible .fav-star': { opacity: 1 },
        // keyboard focus must be plain to see (menu is fully keyboard-driven)
        '&.Mui-focusVisible, &:focus-visible': {
          backgroundColor: alpha(designTokens.colors.primary[500], 0.18),
          outline: `2px solid ${designTokens.colors.primary[400]}`,
          outlineOffset: '-2px',
        },
        '&.Mui-selected': {
          backgroundColor: alpha(designTokens.colors.primary[500], 0.15),
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.2),
          },
        },
        ...(!drawerOpen && {
          borderRadius: '12px',
          border: '1px solid transparent',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.15),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.2),
            '&:hover': {
              backgroundColor: alpha(designTokens.colors.primary[500], 0.25),
            },
          },
        }),
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: level === 1 ? 34 : 26,
          color: isSelected
            ? designTokens.colors.primary[500]
            : designTokens.colors.neutral[400],
          transition: `color ${designTokens.transitions.duration.fast}`,
          ...(!drawerOpen && {
            minWidth: 0,
            mr: 0,
            justifyContent: 'center',
          }),
        }}
      >
        {level === 1 ? (
          <IconRetrieve
            icon={item.icon || 'Icon' + item.title.replace(/\s+|\.*$/g, '')}
            open={isSelected}
          />
        ) : (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isSelected
                ? designTokens.colors.primary[500]
                : designTokens.colors.neutral[500],
              transition: `all ${designTokens.transitions.duration.fast}`,
            }}
          />
        )}
      </ListItemIcon>

      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography
              variant={level === 1 ? 'body2' : 'caption'}
              sx={{
                fontFamily: 'inherit',
                fontWeight: isSelected ? 600 : 500,
                color: isSelected
                  ? designTokens.colors.neutral[50]
                  : designTokens.colors.neutral[300],
                letterSpacing: '0.01em',
                transition: `all ${designTokens.transitions.duration.fast}`,
              }}
            >
              {item.title}
            </Typography>
          }
        />
      )}

      {(drawerOpen || (!drawerOpen && level !== 1)) && String(item.url ?? '').replace(/\/+$/, '') === INBOX && <InboxBadge />}

      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          sx={{
            height: 20,
            fontWeight: 600,
            borderRadius: '6px',
          }}
        />
      )}

      {drawerOpen && item.url && !item.external && <FavStar id={item.id} title={item.title} />}
    </ListItemButton>
  );
});

NavItem.displayName = 'NavItem';

export default NavItem;
